import React, {
  useCallback,
  useEffect,
  useRef,
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import momentTimezone from 'moment-timezone';

import {
  Container,
  ContainerInput,
  Input,
  ContainerSwitcher,
  Toggle,
  ContainerToggle,
  Text,
  ContainerScroll,
  Item,
  ContainerToggleContent,
  ContainerItem,
  ListElementContainer,
} from './styles';

const FORMAT_PM_AM = 'hh:mma';
const FORMAT_24H = 'HH:mm';

const ariaInstructions = 'Select timezone menu. Press Tab to navigate or Esc to close.';

const Content = (props) => {
  const {
    options,
    onChangeToggle,
    onSelectedOption,
    itemSelected,
    onSearch,
    toggleTime,
  } = props;

  const containerRef = useRef(null);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.focus();
    }
  }, [containerRef]);

  const orderedTimezone = [
    'US/CANADA',
    'AMERICA',
    'AFRICA',
    'ASIA',
    'ATLANTIC',
    'AUSTRALIA',
    'UTC',
    'EUROPE',
    'PACIFIC',
  ];

  const handleBlur = () => {
    const menuItems = document.querySelectorAll('.menu .item');
    const focusedItem = document.querySelector('.menu .item:focus');
    const menuVisible = document.querySelector('.menu.visible');
    if (menuItems && focusedItem && menuVisible) {
      // If the focused item is not in the visible dropdown menu, close the menu
      if (!menuVisible.contains(focusedItem)) {
        menuVisible.classList.remove('visible');
      }
    }
  };

  const getCurrentHourTimezone = useCallback((selectedTimezone) => (
    momentTimezone().tz(selectedTimezone).format(
      toggleTime ? FORMAT_24H : FORMAT_PM_AM,
    )
  ), [toggleTime]);

  const timezoneItemsMap = (groupValue) => (
    _.map(options[groupValue], (timezone, index) => {
      const isItemSelected = itemSelected === timezone.unix;
      return (
        <ListElementContainer
          key={`${groupValue + timezone.unix}`}
        >
          {index === 0 && (
            <Item
              key={`Group ${groupValue + timezone.unix}`}
            >
              <Text bold>
                {timezone.group}
              </Text>
            </Item>
          )}

          <Item
            hover
            tabIndex="0"
            key={`Item ${groupValue + timezone.unix}`}
            onClick={() => onSelectedOption(timezone.unix)}
            itemSelected={isItemSelected}
            onBlur={() => handleBlur()}
            role="option"
            aria-selected={isItemSelected}
          >
            <ContainerItem>
              <Text>
                {timezone.label}
              </Text>

              <Text rightMargin leftMargin>
                {getCurrentHourTimezone(timezone.unix)}
              </Text>
            </ContainerItem>
          </Item>
        </ListElementContainer>
      );
    })
  );

  return (
    <Container
      tabIndex="0"
      ref={containerRef}
      aria-label={ariaInstructions}
      role="menu"
    >
      <ContainerInput>
        <Input
          aria-label="Timezone Search"
          placeholder="Search timezone"
          onChange={({ target }) => { onSearch(target.value); }}
          tabIndex={0}
          onBlur={() => handleBlur()}
        />
      </ContainerInput>

      <ContainerSwitcher>
        <Text bold>TIMEZONE</Text>

        <ContainerToggle>
          <Text id="am-pm-hours" aria-label="AM PM / 24 hours toggle. AM PM selected." rightMargin>am/pm</Text>
          <ContainerToggleContent>
            <Toggle
              ariaLabelBy={toggleTime ? '24-hours' : 'am-pm-hours'}
              onChange={onChangeToggle}
              checked={toggleTime}
              id="toggle-key"
              tabIndex="0"
              onBlur={() => handleBlur()}
            />
            <Text id="24-hours" aria-label="AM PM / 24 hours toggle. 24-hours selected." leftMargin>24h</Text>
          </ContainerToggleContent>
        </ContainerToggle>
      </ContainerSwitcher>

      <ContainerScroll>
        {options && (
          _.map(orderedTimezone, (groupValue) => (
            timezoneItemsMap(groupValue)
          ))
        )}
      </ContainerScroll>
    </Container>
  );
};

Content.propTypes = {
  options: PropTypes.shape({}),
  onChangeToggle: PropTypes.func.isRequired,
  onSelectedOption: PropTypes.func.isRequired,
  itemSelected: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  toggleTime: PropTypes.bool.isRequired,
};

Content.defaultProps = {
  options: {},
};

export default Content;
